import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Helmet from 'react-helmet';
import { makeStyles, Theme, createStyles, Grid } from '@material-ui/core';
import { navigate } from '@reach/router';
import ReactMapGL, { Marker, NavigationControl } from 'react-map-gl';
import useOutagesMap, {
  DEFAULT_MAP_LNG,
  DEFAULT_MAP_LAT,
  maxBoundsCoordinates,
} from '../../hooks/useOutagesMap';
import { PGEServiceTerritory } from '../../components/outage/outage-center-map/PGEServiceTerritory';
import { useIsMobile } from '../../util/style-utils';
import config from '../../config';
import { MarkerIcon } from '../../components/static-map';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Maybe } from '../../__generated__/pge-types';
import TextField from '../../components/text-field';
import { useTranslation } from '../../hooks/useTranslation';
import { isLocationWithinPGEServiceBoundary } from '../../util/map-utils';
import Alerts from '../../components/alerts';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';

const apiKey = config.amazonLocationService.key;
const region = config.amazonLocationService.region;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rootContainer: () => ({
      width: '100%',
      height: '100vh',
      position: 'relative',
      '& > *:nth-child(1)': {
        height: '100% !important',
        width: '100% !important',
      },
    }),
    navigationControl: {
      position: 'absolute',
      bottom: 20,
      right: 20,
    },
    searchContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      padding: '0 24px',
      '& .MuiAlert-message': {
        padding: 0,
      },
    },
  }),
);

const useDebounced = (value: string, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

interface SearchSuggestion {
  id: string;
  title: string;
}

const Map = React.memo(
  ({ location }: { location: [number, number] | null }) => {
    const isMobile = useIsMobile();
    const initialViewState = {
      longitude: DEFAULT_MAP_LNG,
      latitude: DEFAULT_MAP_LAT,
      zoom: isMobile ? 6 : 7,
    };

    const { mapOptions, updateViewPort } = useOutagesMap({
      minZoom: 6,
      maxBounds: maxBoundsCoordinates,
    });

    useEffect(() => {
      if (location) {
        updateViewPort({
          latitude: location[1],
          longitude: location[0],
          zoom: 14,
        });
      }
    }, [location]);

    return (
      <ReactMapGL initialViewState={initialViewState} {...mapOptions}>
        <PGEServiceTerritory />
        <NavigationControl
          showZoom
          showCompass={false}
          position="bottom-right"
        />
        {location && (
          <Marker latitude={location[1]} longitude={location[0]}>
            <MarkerIcon />
          </Marker>
        )}
      </ReactMapGL>
    );
  },
);

function PGEServiceAreaMap() {
  const isBrowser = !(typeof window === 'undefined');
  const isNotAnIframe = isBrowser && window.parent.location === window.location;
  isNotAnIframe && navigate('/');

  const { t } = useTranslation();
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<
    [number, number] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { wrapWithLoader } = useWrapWithLoader();
  const skipNextSuggestRef = useRef(false);

  const debouncedSearchQuery = useDebounced(query, 300);

  useEffect(() => {
    if (debouncedSearchQuery.length > 3 && !skipNextSuggestRef.current) {
      void handleSuggest(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  const handleSuggest = async (value: string) => {
    if (value.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        `https://places.geo.${region}.amazonaws.com/v2/suggest?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            QueryText: value,
            BiasPosition: [DEFAULT_MAP_LNG, DEFAULT_MAP_LAT],
            Filter: {
              IncludeCountries: ['US'],
            },
          }),
        },
      );

      setLoading(false);
      const data = await res.json();
      setSuggestions(
        data.ResultItems.map((d: any) => ({
          id: d?.Place?.PlaceId,
          title: d?.Title,
        })) || [],
      );
    } catch (err) {
      setLoading(false);
      setError(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'));
    }
  };

  const handleSelect = wrapWithLoader(
    async (placeId: string, label: string) => {
      setError('');
      setQuery(label);
      setSuggestions([]);

      try {
        const res = await fetch(
          `https://places.geo.${region}.amazonaws.com/v2/place/${placeId}?key=${apiKey}`,
        );
        const data = await res.json();
        skipNextSuggestRef.current = false;

        const coords = data?.Position;
        if (coords) {
          if (
            isLocationWithinPGEServiceBoundary({
              lat: coords[1],
              lng: coords[0],
            })
          ) {
            setSelectedLocation(coords);
          } else {
            setError(t('ADDRESS_OUTSIDE_PGE_SERVICE_TERRITORY'));
          }
        } else {
          setError(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'));
        }
      } catch (err) {
        skipNextSuggestRef.current = false;
        setError(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'));
      }
    },
  );

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className={classes.rootContainer}>
        <Map location={selectedLocation} />
      </div>
      <div className={classes.searchContainer}>
        <Autocomplete
          options={suggestions}
          filterOptions={options => options}
          freeSolo={false}
          inputValue={query}
          getOptionLabel={option => `${option?.title}`}
          style={{ width: '100%' }}
          onChange={(
            event: ChangeEvent<{}>,
            newValue: Maybe<SearchSuggestion>,
          ) => {
            event.preventDefault();
            event.stopPropagation();

            if (newValue?.id) {
              skipNextSuggestRef.current = true;
              void handleSelect(newValue?.id, newValue?.title);
            }
          }}
          onInputChange={async (_, _2, reason: any) => {
            if (reason === 'clear') {
              setQuery('');
              setSelectedLocation(null);
              setError('');
              setSuggestions([]);
            }
          }}
          noOptionsText={t('SEARCH_FOR_AN_ADDRESS')}
          loading={loading}
          renderInput={params => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                maxLength: 60,
                // adding this to disable the browser auto complete so that i doesn't clash with autocomplete component
                //ref: https://stackoverflow.com/questions/48304062/material-ui-textfield-disable-browser-autocomplete
                autoComplete: 'new-password',
              }}
              label={t('SEARCH_FOR_AN_ADDRESS')}
              variant="outlined"
              style={{ width: '100%', marginTop: 20 }}
              data-cy="address-field"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setQuery(e.target.value);
                setError('');
              }}
            />
          )}
          renderOption={option => {
            return (
              <Grid container alignItems="center">
                <Grid item>{`${option?.title}`}</Grid>
              </Grid>
            );
          }}
        />
        {error && (
          <Alerts
            isOpen
            disableColorOverride={true}
            css={{ marginTop: '8px' }}
            message={error}
            severity="error"
          />
        )}
      </div>
    </>
  );
}

export default React.memo(PGEServiceAreaMap);
